/** React */
import React, { ReactNode, createContext, useContext, useState } from "react";

/** Wrapper */
import { MenuWrapper } from "./menu-wrapper";

/** Types */
type Item = {
  id: string;
  isExpanded: boolean;
};

/** Context type */
type MenuContextType = {
  items: Record<string, Item>;
  addItem: (item: Item) => void;
  toggle: (id: string) => void;
};

/** Context */
const MenuContext = createContext<MenuContextType | undefined>(undefined);

/** Props */
type MenuRootProps = {
  children: ReactNode;
  isOpenByDefault?: true;
};

export function MenuRoot({ children, isOpenByDefault }: MenuRootProps) {
  const [items, setItems] = useState<Record<string, Item>>({});

  const addItem = (item: Item) => {
    setItems((state) => {
      if (state[item.id]) return state;

      return {
        ...state,
        [item.id]: {
          ...item,
          isExpanded: isOpenByDefault || false,
        },
      };
    });
  };

  const toggle = (id: string) => {
    setItems((state) => {
      if (!state[id]) return state;

      return {
        ...state,
        [id]: {
          ...state[id],
          isExpanded: !state[id].isExpanded,
        },
      };
    });
  };

  return (
    <MenuContext.Provider value={{ items, addItem, toggle }}>
      <MenuWrapper>{children}</MenuWrapper>
    </MenuContext.Provider>
  );
}

export function useMenu() {
  const context = useContext(MenuContext);

  if (context === undefined) {
    throw new Error("useMenu must be used within a MenuContext Provider");
  }

  return context;
}
